/**
 * Provides a single per-session rule store instance to the subtree. Mount at the
 * plan-detail root so Event / Promo / Plan share one cross-section store that
 * resets when the session unmounts. `rules` come from `useTpoGenConfig().fieldRules`.
 */
import { createContext, useContext, useEffect, useRef, type ReactNode } from 'react';

import type { RuleSet } from '../../config/fieldRulesConfig';
import { validateRuleSet } from '../engine/validate';
import { createRuleStore, type RuleStore } from './createRuleStore';

const RuleStoreContext = createContext<RuleStore | null>(null);

interface FieldRulesProviderProps {
    rules: RuleSet;
    children: ReactNode;
}

export function FieldRulesProvider({ rules, children }: FieldRulesProviderProps) {
    // One store per provider lifetime (per plan-detail session).
    const storeRef = useRef<RuleStore>();
    if (!storeRef.current) storeRef.current = createRuleStore(rules);

    // Dev-time guard: surface malformed qualified ids / reactive write cycles.
    useEffect(() => {
        if (process.env.NODE_ENV === 'production') return;
        const issues = validateRuleSet(rules);
        if (issues.length > 0) {
            // eslint-disable-next-line no-console
            console.error('[fieldRules] validation issues:', issues);
        }
    }, [rules]);

    return <RuleStoreContext.Provider value={storeRef.current}>{children}</RuleStoreContext.Provider>;
}

FieldRulesProvider.displayName = 'FieldRulesProvider';

export function useRuleStoreApi(): RuleStore {
    const store = useContext(RuleStoreContext);
    if (!store) {
        throw new Error('useRuleStoreApi must be used within a FieldRulesProvider');
    }
    return store;
}

/** Non-throwing variant for callers that may render outside a provider. */
export function useOptionalRuleStoreApi(): RuleStore | null {
    return useContext(RuleStoreContext);
}
